// AdminActivityDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination
} from '@mui/material';
import {
  Security,
  Warning,
  Computer,
  Smartphone,
  Tablet,
  CheckCircle,
  Cancel,
  LocationOn,
  Refresh,
  FilterList,
  Close
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from '../utils/axios';
import { formatUserTime } from '../utils/timeFormat';

const AdminActivityDashboard = () => {
  const { user } = useAuth();
  
  // Tab management
  const [currentTab, setCurrentTab] = useState(0);
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [allActivities, setAllActivities] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [allLoginHistory, setAllLoginHistory] = useState([]);
  
  // Loading states
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  
  // Error state
  const [error, setError] = useState('');
  
  // Filter states
  const [activityFilters, setActivityFilters] = useState({
    days: 7,
    activity_type: '',
    risk_level: '',
    user_id: ''
  });
  
  const [loginFilters, setLoginFilters] = useState({
    days: 7,
    login_method: '',
    success_only: null
  });
  
  // Pagination states
  const [activityPage, setActivityPage] = useState(0);
  const [sessionPage, setSessionPage] = useState(0);
  const [loginPage, setLoginPage] = useState(0);
  const rowsPerPage = 10;
  
  // Session termination
  const [terminateConfirmOpen, setTerminateConfirmOpen] = useState(false);
  const [sessionToTerminate, setSessionToTerminate] = useState(null);
  
  // Access control
  const hasAdminAccess = useMemo(() => {
    return user?.role === 'super_admin' || user?.role === 'admin';
  }, [user?.role]);

  // Data loading functions
  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setError('');
    try {
      const response = await axios.get('/activity/admin/summary');
      setSummary(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadActivities = useCallback(async () => {
    setActivitiesLoading(true);
    setError('');
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - activityFilters.days);
      
      const params = {
        limit: 1000,
        start_date: startDate.toISOString()
      };
      
      if (activityFilters.activity_type) {
        params.activity_type = activityFilters.activity_type;
      }
      if (activityFilters.risk_level) {
        params.risk_level = activityFilters.risk_level;
      }
      if (activityFilters.user_id) {
        params.user_id = activityFilters.user_id;
      }
      
      const response = await axios.get('/activity/admin/activities', { params });
      setAllActivities(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load activities');
    } finally {
      setActivitiesLoading(false);
    }
  }, [activityFilters.days, activityFilters.activity_type, activityFilters.risk_level, activityFilters.user_id]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    setError('');
    try {
      const params = {
        limit: 500,
        active_only: true
      };
      
      const response = await axios.get('/activity/admin/sessions', { params });
      setAllSessions(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const loadLoginHistory = useCallback(async () => {
    setLoginHistoryLoading(true);
    setError('');
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - loginFilters.days);
      
      const params = {
        limit: 1000,
        start_date: startDate.toISOString()
      };
      
      if (loginFilters.login_method) {
        params.login_method = loginFilters.login_method;
      }
      if (loginFilters.success_only !== null) {
        params.success_only = loginFilters.success_only;
      }
      
      const response = await axios.get('/activity/admin/login-history', { params });
      setAllLoginHistory(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load login history');
    } finally {
      setLoginHistoryLoading(false);
    }
  }, [loginFilters.days, loginFilters.login_method, loginFilters.success_only]);

  // Load data when filters change or tab changes
  useEffect(() => {
    if (currentTab === 0) {
      loadSummary();
    } else if (currentTab === 1) {
      loadActivities();
    } else if (currentTab === 2) {
      loadSessions();
    } else if (currentTab === 3) {
      loadLoginHistory();
    }
  }, [currentTab, loadSummary, loadActivities, loadSessions, loadLoginHistory]);

  // Event handlers
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleActivityFilterChange = (name, value) => {
    setActivityFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setActivityFilters({
      days: 7,
      activity_type: '',
      risk_level: '',
      user_id: ''
    });
  };

  const handleTerminateSession = (session) => {
    setSessionToTerminate(session);
    setTerminateConfirmOpen(true);
  };

  // Utility functions
  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return formatUserTime(timestamp, user);
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '-';
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile':
        return <Smartphone />;
      case 'tablet':
        return <Tablet />;
      default:
        return <Computer />;
    }
  };

  const confirmTerminateSession = async () => {
    if (!sessionToTerminate) return;
    
    try {
      await axios.delete(`/activity/admin/sessions/${sessionToTerminate.session_id}`);
      setTerminateConfirmOpen(false);
      setSessionToTerminate(null);
      loadSessions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to terminate session');
    }
  };

  // Summary Tab Content
  const renderSummaryTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Activity Overview</Typography>
        <Button
          onClick={loadSummary}
          disabled={summaryLoading}
          startIcon={<Refresh />}
        >
          Refresh
        </Button>
      </Box>

      {summaryLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : summary ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Users
                </Typography>
                <Typography variant="h4">
                  {summary.total_users || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Active Sessions
                </Typography>
                <Typography variant="h4">
                  {summary.active_sessions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Activities (24h)
                </Typography>
                <Typography variant="h4">
                  {summary.activities_today || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Failed Logins (24h)
                </Typography>
                <Typography variant="h4" color="error">
                  {summary.failed_logins_today || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Typography>No summary data available</Typography>
      )}
    </Box>
  );

  // Activities Tab Content
  const renderActivitiesTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">User Activities</Typography>
        <Box display="flex" gap={1}>
          <Button onClick={handleResetFilters} startIcon={<FilterList />}>
            Reset Filters
          </Button>
          <Button onClick={loadActivities} disabled={activitiesLoading} startIcon={<Refresh />}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Days</InputLabel>
            <Select
              value={activityFilters.days}
              label="Days"
              onChange={(e) => handleActivityFilterChange('days', e.target.value)}
            >
              <MenuItem value={1}>Last 24 hours</MenuItem>
              <MenuItem value={7}>Last 7 days</MenuItem>
              <MenuItem value={30}>Last 30 days</MenuItem>
              <MenuItem value={90}>Last 90 days</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Activity Type</InputLabel>
            <Select
              value={activityFilters.activity_type}
              label="Activity Type"
              onChange={(e) => handleActivityFilterChange('activity_type', e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="login">Login</MenuItem>
              <MenuItem value="logout">Logout</MenuItem>
              <MenuItem value="mfa_setup">MFA Setup</MenuItem>
              <MenuItem value="password_change">Password Change</MenuItem>
              <MenuItem value="profile_update">Profile Update</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Risk Level</InputLabel>
            <Select
              value={activityFilters.risk_level}
              label="Risk Level"
              onChange={(e) => handleActivityFilterChange('risk_level', e.target.value)}
            >
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {activitiesLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Risk Level</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allActivities
                .slice(activityPage * rowsPerPage, activityPage * rowsPerPage + rowsPerPage)
                .map((activity, index) => (
                <TableRow key={index}>
                  <TableCell>{activity.user_email || activity.user_id}</TableCell>
                  <TableCell>
                    <Chip 
                      label={activity.activity_type} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{activity.description}</TableCell>
                  <TableCell>
                    <Chip 
                      label={activity.risk_level} 
                      size="small" 
                      color={getRiskColor(activity.risk_level)}
                    />
                  </TableCell>
                  <TableCell>{activity.ip_address || '-'}</TableCell>
                  <TableCell>{formatTimestamp(activity.timestamp)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={allActivities.length}
            page={activityPage}
            onPageChange={(event, newPage) => setActivityPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      )}
    </Box>
  );

  // Active Sessions Tab Content
  const renderActiveSessionsTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Active Sessions</Typography>
        <Button onClick={loadSessions} disabled={sessionsLoading} startIcon={<Refresh />}>
          Refresh
        </Button>
      </Box>

      {sessionsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allSessions
                .slice(sessionPage * rowsPerPage, sessionPage * rowsPerPage + rowsPerPage)
                .map((session, index) => (
                <TableRow key={index}>
                  <TableCell>{session.user_email || session.user_id}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getDeviceIcon(session.device_info?.type)}
                      <Box>
                        <Typography variant="body2">
                          {session.device_info?.browser || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {session.device_info?.os || 'Unknown OS'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationOn fontSize="small" />
                      {session.location?.city || 'Unknown'}
                    </Box>
                  </TableCell>
                  <TableCell>{session.ip_address}</TableCell>
                  <TableCell>{formatRelativeTime(session.created_at)}</TableCell>
                  <TableCell>{formatRelativeTime(session.last_activity)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => handleTerminateSession(session)}
                    >
                      Terminate
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={allSessions.length}
            page={sessionPage}
            onPageChange={(event, newPage) => setSessionPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      )}
    </Box>
  );

  // Login History Tab Content
  const renderLoginHistoryTab = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Login History</Typography>
        <Button onClick={loadLoginHistory} disabled={loginHistoryLoading} startIcon={<Refresh />}>
          Refresh
        </Button>
      </Box>

      {loginHistoryLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Risk Score</TableCell>
                <TableCell>MFA</TableCell>
                <TableCell>Timestamp</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allLoginHistory
                .slice(loginPage * rowsPerPage, loginPage * rowsPerPage + rowsPerPage)
                .map((login, index) => (
                <TableRow key={index}>
                  <TableCell>{login.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={login.login_method} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={login.success ? 'Success' : 'Failed'} 
                      size="small" 
                      color={login.success ? 'success' : 'error'}
                      icon={login.success ? <CheckCircle /> : <Cancel />}
                    />
                  </TableCell>
                  <TableCell>{login.ip_address}</TableCell>
                  <TableCell>
                    {login.risk_score !== null && (
                      <Chip 
                        label={`${login.risk_score}%`} 
                        size="small" 
                        color={login.risk_score > 70 ? 'error' : login.risk_score > 40 ? 'warning' : 'success'}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {login.mfa_required ? (
                      <Chip 
                        label={login.mfa_completed ? 'Completed' : 'Required'} 
                        size="small" 
                        color={login.mfa_completed ? 'success' : 'warning'}
                        icon={<Security />}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{formatTimestamp(login.timestamp)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={allLoginHistory.length}
            page={loginPage}
            onPageChange={(event, newPage) => setLoginPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      )}
    </Box>
  );

  // Access control check
  if (!hasAdminAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>
            You need administrator privileges to access the activity dashboard.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Activity Dashboard
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          {user.role === 'super_admin' 
            ? 'Monitor activity across all users and organizations.'
            : 'Monitor activity for users in your organization.'
          }
        </Typography>

        <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab label="Overview" />
          <Tab label="Activities" />
          <Tab label="Active Sessions" />
          <Tab label="Login History" />
          <Tab label="Suspicious Activity" />
        </Tabs>

        {currentTab === 0 && renderSummaryTab()}
        {currentTab === 1 && renderActivitiesTab()}
        {currentTab === 2 && renderActiveSessionsTab()}
        {currentTab === 3 && renderLoginHistoryTab()}
        {currentTab === 4 && (
          <Box>
            <Typography variant="h6">Suspicious Activity</Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              Suspicious activity monitoring will be displayed here.
            </Alert>
          </Box>
        )}
      </Paper>

      {/* Terminate Session Confirmation Dialog */}
      <Dialog 
        open={terminateConfirmOpen} 
        onClose={(event, reason) => {
          if (reason === 'escapeKeyDown') {
            setTerminateConfirmOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Warning color="warning" />
              Terminate Session
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => setTerminateConfirmOpen(false)}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to terminate this user session? This will immediately log out the user from their device.
          </Typography>
          {sessionToTerminate && (
            <Box mt={2} p={2} bgcolor="background.paper" borderRadius={1} border="1px solid" borderColor="divider">
              <Typography variant="subtitle2">
                {sessionToTerminate.user_email || 'Unknown User'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {sessionToTerminate.device_info?.browser || 'Unknown Browser'} on {sessionToTerminate.device_info?.os || 'Unknown OS'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {sessionToTerminate.ip_address} • Active since {formatRelativeTime(sessionToTerminate.created_at)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminateConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={confirmTerminateSession} color="warning" variant="contained">
            Terminate Session
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminActivityDashboard;